import { useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import { useLocation } from "react-router";
import { toast } from "sonner";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { cn } from "@/lib/utils";
import { useOptionalAccountWorkspace } from "@/lib/account-workspace";
import { brandHasTool } from "@/lib/modules";
import { ensureBrandSite } from "@/lib/brand-site-api";
import { createKbApi, type KbWiki } from "@/lib/kb-api";
import {
  parseJourneyAuthoringRoute,
  type JourneyAuthoringContext,
} from "@/lib/hermes-journey-authoring";
import { type BrandSiteAuthoringContext } from "@/lib/hermes-brand-site-authoring";
import { type PlatformTemplateIdeContext } from "@/lib/hermes-platform-template-ide";
import {
  capabilityCount,
  capabilityCountLabel,
  type BrandToolBinding,
  type CapabilityDraft,
} from "@/lib/hermes-capability-bindings";
import {
  buildBrandSiteStudioLockedDraft,
  mergeBrandSiteStudioFloor,
  type BrandSiteStudioPanelLock,
} from "@/lib/hermes-brand-site-studio";
import { HermesCapabilityPanel } from "./HermesCapabilityPanel";

type HermesCapabilityBarProps = {
  accountId: string;
  draft: CapabilityDraft;
  onDraftChange?: (draft: CapabilityDraft, options?: { persist?: boolean }) => void;
  journeyAuthoring: JourneyAuthoringContext | null;
  /** 关闭旅程后仍可用于再开的最近创作上下文 */
  lastJourneyAuthoring?: JourneyAuthoringContext | null;
  onStartJourneyAuthoring: (ctx: JourneyAuthoringContext) => void | Promise<void>;
  onStopJourneyAuthoring: () => void;
  brandSiteAuthoring: BrandSiteAuthoringContext | null;
  lastBrandSiteAuthoring?: BrandSiteAuthoringContext | null;
  onStartBrandSiteAuthoring: (ctx: BrandSiteAuthoringContext) => void;
  onStopBrandSiteAuthoring: () => void;
  platformTemplateIde?: PlatformTemplateIdeContext | null;
  /** 设计工作室：必选能力钉死底限，面板仍可加码 */
  brandSiteStudioActive?: boolean;
  onStopPlatformTemplateIde?: () => void;
  disabled?: boolean;
  className?: string;
};

const capabilityButtonBaseClass =
  "inline-flex h-6 w-[4.75rem] shrink-0 items-center justify-center rounded-md border px-1.5 text-[11px] font-medium tabular-nums transition-colors";

function writableBrandId(bindings: BrandToolBinding[]): number | null {
  return bindings.find((row) => row.access === "write")?.brand_id ?? null;
}

function wikiChipLabels(wikis: KbWiki[]): {
  enterpriseWikiId?: number;
  brandWikiByBrandId: Record<number, number>;
} {
  const brandWikiByBrandId: Record<number, number> = {};
  let enterpriseWikiId: number | undefined;
  for (const wiki of wikis) {
    if (wiki.kind === "enterprise") {
      enterpriseWikiId = wiki.id;
      continue;
    }
    if (wiki.kind === "brand" && wiki.brand_id != null && wiki.brand_id > 0) {
      brandWikiByBrandId[wiki.brand_id] = wiki.id;
    }
  }
  return { enterpriseWikiId, brandWikiByBrandId };
}

/** 输入框下方：固定宽度「能力 x N」计数按钮，点击打开能力面板。 */
export function HermesCapabilityBar({
  accountId,
  draft,
  onDraftChange,
  journeyAuthoring,
  lastJourneyAuthoring = null,
  onStartJourneyAuthoring,
  onStopJourneyAuthoring,
  brandSiteAuthoring,
  lastBrandSiteAuthoring = null,
  onStartBrandSiteAuthoring,
  onStopBrandSiteAuthoring,
  platformTemplateIde = null,
  brandSiteStudioActive = false,
  disabled = false,
  className,
}: HermesCapabilityBarProps) {
  const location = useLocation();
  const workspace = useOptionalAccountWorkspace();
  const [panelOpen, setPanelOpen] = useState(false);
  const [brandSiteBusyBrandId, setBrandSiteBusyBrandId] = useState<number | null>(null);
  const [wikis, setWikis] = useState<KbWiki[]>([]);
  const locked = disabled || !onDraftChange;
  /** 平台模板 IDE：完全禁止改能力 */
  const ideLocked = Boolean(platformTemplateIde);

  const routeJourney = useMemo(
    () => parseJourneyAuthoringRoute(location.pathname, accountId),
    [location.pathname, accountId],
  );

  useEffect(() => {
    if (!accountId) {
      setWikis([]);
      return;
    }
    let cancelled = false;
    void createKbApi(accountId)
      .listWikis()
      .then(({ wikis: loaded }) => {
        if (!cancelled) setWikis(loaded);
      })
      .catch(() => {
        if (!cancelled) setWikis([]);
      });
    return () => {
      cancelled = true;
    };
  }, [accountId]);

  const brands = workspace?.brands ?? [];
  const accountName = workspace?.account?.name ?? "当前账户";
  const { enterpriseWikiId, brandWikiByBrandId } = useMemo(() => wikiChipLabels(wikis), [wikis]);
  const journeyBrandIds = useMemo(
    () => brands.filter((brand) => brandHasTool(brand, "journey")).map((brand) => brand.id),
    [brands],
  );
  const brandSiteBrandIds = useMemo(
    () => brands.filter((brand) => brandHasTool(brand, "brand_site")).map((brand) => brand.id),
    [brands],
  );
  const capabilityTotal = capabilityCount(draft);
  const capabilityLabel = capabilityCountLabel(capabilityTotal);

  const studioLock = useMemo((): BrandSiteStudioPanelLock | null => {
    if (!brandSiteStudioActive || !brandSiteAuthoring) return null;
    const brandWikiId = brandWikiByBrandId[brandSiteAuthoring.brandId];
    const kbBindings =
      brandWikiId != null ? [{ wiki_id: brandWikiId, access: "read" as const }] : [];
    return {
      brandId: brandSiteAuthoring.brandId,
      floor: buildBrandSiteStudioLockedDraft({
        brandId: brandSiteAuthoring.brandId,
        brandSiteId: brandSiteAuthoring.brandSiteId,
        enterpriseWikiId,
        kbBindings,
      }),
    };
  }, [brandSiteStudioActive, brandSiteAuthoring, brandWikiByBrandId, enterpriseWikiId]);

  function applyStudioFloor(next: CapabilityDraft): CapabilityDraft {
    if (!studioLock) return next;
    return mergeBrandSiteStudioFloor(next, studioLock.floor);
  }

  function handleSelectJourneyBrand(brandId: number) {
    if (locked || ideLocked) return Promise.resolve();
    const journeyId =
      routeJourney?.brandId === brandId
        ? routeJourney.journeyId
        : lastJourneyAuthoring?.brandId === brandId
          ? lastJourneyAuthoring.journeyId
          : journeyAuthoring?.brandId === brandId
            ? journeyAuthoring.journeyId
            : null;
    return onStartJourneyAuthoring({
      mode: "journey_authoring",
      accountId,
      brandId,
      journeyId,
    });
  }

  async function handleSelectBrandSiteBrand(brandId: number): Promise<boolean> {
    if (locked || ideLocked) return false;
    if (brandSiteAuthoring?.brandId === brandId) return true;
    if (lastBrandSiteAuthoring?.brandId === brandId && lastBrandSiteAuthoring.brandSiteId) {
      onStartBrandSiteAuthoring(lastBrandSiteAuthoring);
      return true;
    }
    setBrandSiteBusyBrandId(brandId);
    try {
      const payload = await ensureBrandSite(accountId, brandId);
      onStartBrandSiteAuthoring({
        mode: "brand_site_authoring",
        accountId,
        brandId,
        brandSiteId: payload.brand_site.id,
      });
      return true;
    } catch (error) {
      toast.error(error instanceof Error ? error.message : "没法打开这个品牌的官网");
      return false;
    } finally {
      setBrandSiteBusyBrandId(null);
    }
  }

  async function commitDraft(next: CapabilityDraft) {
    if (locked || ideLocked || !onDraftChange) return;
    const merged = applyStudioFloor(next);

    const prevJourneyWrite = writableBrandId(draft.journey);
    const nextJourneyWrite = writableBrandId(merged.journey);
    const prevSiteWrite = writableBrandId(draft.brandSite);
    const nextSiteWrite = writableBrandId(merged.brandSite);

    if (nextSiteWrite != null && nextSiteWrite !== prevSiteWrite) {
      if (studioLock) return;
      const started = await handleSelectBrandSiteBrand(nextSiteWrite);
      // ensureBrandSite 失败时不落地 write 草稿
      if (!started) return;
    }

    const startingJourney = nextJourneyWrite != null && nextJourneyWrite !== prevJourneyWrite;
    const stoppingJourney = prevJourneyWrite != null && nextJourneyWrite == null;

    if (startingJourney) {
      if (studioLock) {
        // 设计工作室：只加旅程绑定，不切 journey_authoring、不打开全局 Hermes
        onDraftChange(merged);
      } else {
        // 先本地应用；start 常会换/清空 chat，绑定要写到结果会话
        onDraftChange(merged, { persist: false });
        await handleSelectJourneyBrand(nextJourneyWrite);
        onDraftChange(merged);
      }
    } else {
      onDraftChange(merged);
      if (stoppingJourney && !studioLock) onStopJourneyAuthoring();
    }

    if (!studioLock && prevSiteWrite != null && nextSiteWrite == null) {
      onStopBrandSiteAuthoring();
    }
  }

  const countButtonClass = cn(
    capabilityButtonBaseClass,
    capabilityTotal > 0
      ? "border-primary/45 bg-primary/15 text-foreground hover:bg-primary/20 dark:border-primary/55 dark:bg-primary/30 dark:text-primary-foreground"
      : "border-dashed border-border text-muted-foreground hover:border-border hover:bg-muted/50 hover:text-foreground",
    locked && "cursor-not-allowed opacity-70",
  );

  return (
    <div className={cn("flex min-w-0 items-center", className)}>
      {panelOpen && typeof document !== "undefined"
        ? createPortal(
            <button
              type="button"
              aria-label="关闭能力面板"
              className="fixed inset-0 z-[49] bg-black/25 supports-backdrop-filter:backdrop-blur-[2px] data-[state=open]:animate-in data-[state=open]:fade-in-0"
              onClick={() => setPanelOpen(false)}
            />,
            document.body,
          )
        : null}

      {ideLocked || locked ? (
        <span
          className={countButtonClass}
          aria-label={
            capabilityTotal > 0 ? `已开启 ${capabilityTotal} 项能力` : "添加能力"
          }
        >
          {capabilityLabel}
        </span>
      ) : (
        <Popover open={panelOpen} onOpenChange={setPanelOpen} modal>
          <PopoverTrigger asChild>
            <button
              type="button"
              aria-label={
                capabilityTotal > 0
                  ? `管理本轮能力，已开启 ${capabilityTotal} 项`
                  : "添加能力"
              }
              aria-haspopup="dialog"
              aria-expanded={panelOpen}
              className={countButtonClass}
            >
              {capabilityLabel}
            </button>
          </PopoverTrigger>
          <PopoverContent
            align="start"
            side="top"
            sideOffset={8}
            className="z-[51] w-auto overflow-hidden rounded-xl border border-border p-0 shadow-2xl ring-1 ring-black/5"
          >
            <HermesCapabilityPanel
              accountName={accountName}
              brands={brands.map((brand) => ({ id: brand.id, name: brand.name }))}
              draft={draft}
              onDraftChange={(next) => {
                void commitDraft(next);
              }}
              enterpriseWikiId={enterpriseWikiId}
              brandWikiByBrandId={brandWikiByBrandId}
              journeyBrandIds={journeyBrandIds}
              brandSiteBrandIds={brandSiteBrandIds}
              brandSiteBusyBrandId={brandSiteBusyBrandId}
              disabled={locked}
              studioLock={studioLock}
              onClose={() => setPanelOpen(false)}
            />
          </PopoverContent>
        </Popover>
      )}
    </div>
  );
}
